
import { ROUTES, ROUTES_INFORMATION, ABSOLUTE_ROUTE_START  } from "../../constants/routes";
import { PRODUCT_SKU_REGEX, ENDING_FORWARD_SLASHES } from "../../constants/regex";
import { PAGE_DELIVERY_KEYS } from "../../constants/cms";
import { isAtLeastOneFunctionTruthy } from "../dataStructure/arrayOperations";
import publishedCmsPages from "../../public/cms-schemas/published-pages.json";
import { EXPONDO_SKU_PREFIX } from "../../constants/products";
import { isPrefix, getPrefix, hasPrefix } from "./prefixes/index";
import { Logger } from '~~/helpers/logger';
import type { PublishedCmsPages } from '~~/helpers/cms/publishedCmsPages';
/**
 * @param {keyof ROUTES_INFORMATION} routeIdentifier
 * @returns string
 */
function getSlugForRoute(routeIdentifier) {

  return getPageLinkForSlug(ROUTES_INFORMATION[routeIdentifier].SLUG);

}
/**
 * @param {keyof ROUTES_INFORMATION} routeIdentifier
 * @returns string
 */
function getNameForRoute(routeIdentifier) {

  return ROUTES_INFORMATION[routeIdentifier].NAME;

}
/**
 * @param {{ slug: string, hasEndingSlash: boolean }} parameters
 * @returns string
 */
function slugToAbsolutePath({ slug, hasEndingSlash }) {

  if (slug.startsWith('http') || slug.startsWith('/')) {

    return slug;

  }

  return `/${slug}${hasEndingSlash ? '/' : ''}`;

}
/**
 * @param {string} path
 * @returns {boolean}
 */
function isHomePageSlug(path) {

  const pathElements = path.replace(ENDING_FORWARD_SLASHES, '').split('/');
  const slug = pathElements[pathElements.length - 1];

  return slug === ROUTES_INFORMATION[ROUTES.HOME].SLUG || slug === '' || isPrefix(slug);

}
/**
 * @param {string} slug
 * @returns {string | undefined}
 */
function getProductSku(slug) {

  if (!slug)
  {

    return;

  }
  const isSlugWithEndSlash = slug.slice(-1) === '/';
  const url = isSlugWithEndSlash ? slug.slice(0, -1) : slug;
  const match = PRODUCT_SKU_REGEX.exec(url);

  if (match?.[1]) {

    return `${EXPONDO_SKU_PREFIX}${match[1]}`;

  }

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isProductPageSlug(slug) {

  const sku = getProductSku(slug);

  if (!sku) {

    return false;

  }

  return true;

}
/**
 * @param {string} slug
 * @returns {number}
 */
function slugCheckStartIndex(slug) {

  const slugIndexAfterPrefix = 3;

  return hasPrefix(slug) ? slugIndexAfterPrefix : 0;

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isSearchPageSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.SEARCH].SLUG, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isAddressesPageSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.CHECKOUT_ADDRESSES].SLUG, slugCheckStartIndex(slug));

}
function isPaymentPageSlug(slug: string): boolean {

  const paymentSlug = ROUTES_INFORMATION[ROUTES.CHECKOUT_PAYMENT]?.SLUG;

  return !!paymentSlug && slug.startsWith(paymentSlug, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isNewsletterPageSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.NEWSLETTER_CONFIRMATION].SLUG, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isNewsletterVoucherPageSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.NEWSLETTER_CONFIRMATION_VOUCHER].SLUG, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isBuyExtraGuaranteePageSlug(slug: string): boolean {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.BUY_EXTRA_GUARANTEE]?.SLUG ?? '', slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isEmailVerificationPageSlug(slug?: string) {

  return slug?.startsWith(ROUTES_INFORMATION[ROUTES.VERIFY_EMAIL].SLUG, slugCheckStartIndex(slug)) ?? false;

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isLoginPageSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.LOGIN].SLUG, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isCheckoutPageSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.CHECKOUT].SLUG, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isMyAccountPageSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.MY_ACCOUNT].SLUG, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isResetPasswordPageSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.RESET_PASSWORD].SLUG, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isCreateAccountPageSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.CREATE_ACCOUNT].SLUG, slugCheckStartIndex(slug));

}

function isSharedBasketPageSlug(slug: string) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.SHARED_BASKET].SLUG, slugCheckStartIndex(slug));

}

type IsValidCmsPageParams = {
  slug: string | string[] | null | undefined,
  languageAndCountry: string,
  publishedPages?: PublishedCmsPages,
};

function isValidCmsPage({
  slug,
  languageAndCountry,
  publishedPages = publishedCmsPages,
}: IsValidCmsPageParams) {

  const checkedSlug = slug || PAGE_DELIVERY_KEYS.HOME.SLUG;
  const checkedFileName = `${checkedSlug}-${languageAndCountry}`;

  return !!publishedPages[checkedFileName];

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isUnindexedPageSlug(slug) {

  const unindexedPageChecks = [
    isLoginPageSlug,
    isCheckoutPageSlug,
    isMyAccountPageSlug,
    isResetPasswordPageSlug,
    isCreateAccountPageSlug,
    isSearchPageSlug,
    isSharedBasketPageSlug,
  ];

  return isAtLeastOneFunctionTruthy(unindexedPageChecks, slug);

}
/**
 * @param {string} str
 * @returns {string}
 */
function removeStartingAndEndingSlash(str) {

  if (str.startsWith('/')) {

    str = str.substring(1);

  }

  if (str.endsWith('/')) {

    str = str.substring(0, str.length - 1);

  }

  return str;

}
/**
 * @param {string} slug
 * @returns {string}
 */
function getPageLinkForSlug(slug) {

  if (!slug) {

    Logger.info('undefined slug detected');

    return '';

  }
  const slugKeywords = removeStartingAndEndingSlash(slug);
  const prefix = getPrefix(slug);
  let testedSlug = slug;

  if (prefix) {

    testedSlug = slug.replace(`/${prefix}`, '');

  }

  if (isHomePageSlug(testedSlug)) {

    return slugToAbsolutePath({ slug: prefix || '', hasEndingSlash: prefix });

  }
  const shouldEndWithSlash = !isProductPageSlug(testedSlug) && !isUnindexedPageSlug(testedSlug);

  return slugToAbsolutePath({
    slug: slugKeywords,
    hasEndingSlash: shouldEndWithSlash,
  });

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isHiddenRouteSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.HIDDEN].SLUG, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isBuildRouteSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.BUILD].SLUG, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @returns {boolean}
 */
function isApiRouteSlug(slug) {

  return slug.startsWith(ROUTES_INFORMATION[ROUTES.API].SLUG, slugCheckStartIndex(slug));

}
/**
 * @param {string} slug
 * @param { import('vue-router/types').RouteConfig } routeMap
 * @returns {boolean}
 */
function isHardcodedRoute(slug, routeMap) {

  const hardcodedRoutes = routeMap.filter(route => !route.path.startsWith(ABSOLUTE_ROUTE_START));

  return hardcodedRoutes.some(route => {

    return slug.match(route.regex);

  });

}
/**
 * @param {string} slug
 * @param { import('vue-router/types').RouteConfig } routeMap
 * @returns {boolean}
 */
function isConstantRoute(slug, routeMap) {

  const constantSlugChecks = [
    isHardcodedRoute,
    isHiddenRouteSlug,
    isBuildRouteSlug,
    isApiRouteSlug,
  ];

  return isAtLeastOneFunctionTruthy(constantSlugChecks, slug, routeMap);

}
export { isHomePageSlug };
export { isProductPageSlug };
export { isSearchPageSlug };
export { isValidCmsPage };
export { isLoginPageSlug };
export { isUnindexedPageSlug };
export { getPageLinkForSlug };
export { getSlugForRoute };
export { getNameForRoute };
export { slugToAbsolutePath };
export { isAddressesPageSlug };
export { isPaymentPageSlug };
export { isNewsletterPageSlug };
export { isNewsletterVoucherPageSlug };
export { isBuyExtraGuaranteePageSlug };
export { isCheckoutPageSlug };
export { isEmailVerificationPageSlug };
export { isConstantRoute };
export { getProductSku };
